import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useData } from 'vike-react/useData';
import { usePageContext } from 'vike-react/usePageContext';
import {
  MapPin, Bed, Bath, Maximize, Heart, Share, ArrowLeft,
  Grid3X3, MessageCircle, Phone, Calendar, Check, CheckCircle, CheckCircle2, Clock, Home,
  ChevronLeft, ChevronRight, ChevronUp, ChevronDown, X, ExternalLink, Loader2,
  Calculator, TrendingDown, TrendingUp, Info, Building, Building2, Receipt, Mail,
  // Amenity icons
  Waves, TreePine, Shield, Wind, Car, Dumbbell, Sun, Armchair, Droplets, Zap,
  Route, Users, Flame, Wifi, Banknote,
  // NEW icons for Kenya-market sections
  Camera, UserCheck, Bell, ArrowUpDown, MapPinned, FileCheck, Baby,
  type LucideIcon
} from 'lucide-react';
import type { PropertyPageData } from './+data';

// Client-side tracking imports (called only in useEffect, SSR-safe)
import { trackEvent } from '../../../../utils/analytics';
import { sendGaEvent } from '../../../../utils/gaGuard';
import { addToRecentlyViewed } from '../../../../utils/recentlyViewed';

// Client-side interactive components
import ShareModal from '../../../../components/ShareModal';
import ContactAgentModalSSR from '../../../components/ContactAgentModalSSR';
// ASSIST-01: client-only card (renders nothing on the server, brief R2a.5).
import AssistCard from '../../../../components/assist/AssistCard';
import { rememberReturnAfterAuth } from '../../../../lib/authReturn';
import ImageLightbox from '../../../../components/ui/ImageLightbox';
import MobileAppPrompt from '../../../components/MobileAppPrompt';

// SSR-safe layout components
import HeaderSSR from '../../../components/HeaderSSR';
import UsdApprox from '../../../../components/UsdApprox';
import { formatKesAmount, formatListingPrice } from '../../../../utils/formatCurrency';
import FooterSSR from '../../../components/FooterSSR';

// Client-side save API (PostgREST via lib/api/client, browser-only).
// SYND-15: every function takes a SavedTarget. This page is the marketplace
// listing, so the kind is always `listing`.
import {
  getSavedTargets,
  saveProperty,
  unsaveProperty,
  savedListing,
  savedTargetKey,
} from '../../../../lib/api/savedPropertiesApi';

// Supabase client for auth check (lazy proxy, SSR-safe)
import { supabase } from '../../../../lib/supabaseClient';

// Leaflet lazy-loader (CDN script injection, browser-only)
import { loadLeaflet } from '../../../../utils/loadLeaflet';

// Mortgage calculation (pure function, SSR-safe)
import { calculateMonthlyPayment } from '../../../../types';

// =============================================
// GA4 TRACKING
//
// GA4-01: the five gtag calls in this file used to reach `window.gtag` directly,
// each with its own `typeof window` check, so none of them saw the route guard.
// They now go through `sendGaEvent`, which drops the hit on a blocked route and
// stamps the sanitized `page_location`. utils/gaGuard.ts pulls in nothing (in
// particular no react-router-dom), so the reason this was inlined in the first
// place does not apply to it.
// =============================================
function trackGA4ViewItem(property: {
  id: string; title: string; price: number;
  currency: string; type: string; city: string;
}): void {
  sendGaEvent('view_item', {
    item_id: property.id,
    item_name: property.title,
    price: property.price,
    currency: property.currency || 'KES',
    item_category: property.type,
    item_variant: property.city,
  });
}

// =============================================
// FORMAT HELPERS FOR NEW KENYA-MARKET FIELDS
// =============================================

// Format floor level with ordinal suffix
function formatFloorLevel(floor: number | null | undefined): string | null {
  if (floor === null || floor === undefined) return null;
  if (floor === 0) return 'Ground Floor';
  if (floor === 1) return '1st Floor';
  if (floor === 2) return '2nd Floor';
  if (floor === 3) return '3rd Floor';
  return `${floor}th Floor`;
}

// Format title deed with leasehold years
function formatTitleDeed(type: string | null | undefined, yearsRemaining: number | null | undefined): string | null {
  if (!type) return null;
  const typeLabels: Record<string, string> = {
    freehold: 'Freehold',
    leasehold: 'Leasehold',
    sectional: 'Sectional Title',
  };
  const label = typeLabels[type] || type;
  if (type === 'leasehold' && yearsRemaining) {
    return `${label} (${yearsRemaining} years remaining)`;
  }
  return label;
}

// Format road access type
function formatRoadAccess(type: string | null | undefined): string | null {
  if (!type) return null;
  const labels: Record<string, string> = {
    tarmac: 'Tarmac Road',
    murram: 'Murram / Gravel',
    all_weather: 'All-Weather Road',
    earth: 'Earth / Dirt Road',
  };
  return labels[type] || type;
}

// Format zoning type
function formatZoning(type: string | null | undefined): string | null {
  if (!type) return null;
  const labels: Record<string, string> = {
    residential: 'Residential',
    commercial: 'Commercial',
    agricultural: 'Agricultural',
    mixed_use: 'Mixed Use',
    industrial: 'Industrial',
  };
  return labels[type] || type;
}

// =============================================
// FORMAT HELPERS (SSR-safe)
// =============================================
// CURR-03: the price label is no longer formatted here. This file used to carry
// its own copy of formatCurrency, which is how the SSR tree and the SPA tree
// could drift apart on the same screen — and both are reachable for the same
// property, depending only on whether the buyer arrived by link or by
// navigation. It now shares utils/formatCurrency with everything else.

function formatPropertyType(type: string): string {
  const typeMap: Record<string, string> = {
    apartment: 'Apartment', villa: 'Villa', townhouse: 'Townhouse',
    bungalow: 'Bungalow', plot: 'Plot', land: 'Land', house: 'House',
    commercial: 'Commercial', new_development: 'New Development',
  };
  return typeMap[type?.toLowerCase()] ||
    type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Property';
}

// =============================================
// MAIN COMPONENT
// =============================================
export default function PropertyPage() {
  const property = useData<PropertyPageData>();

  const images = property.images?.length ? property.images
    : property.image ? [property.image] : [];

  const isProject = property.isParentListing;

  // Price per m²
  const pricePerSqm = property.area > 0 ? Math.round(property.price / property.area) : null;

  // Days on market
  const daysOnMarket = (() => {
    if (!property.createdAt) return null;
    const created = new Date(property.createdAt);
    const now = new Date();
    return Math.ceil(Math.abs(now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24));
  })();

  // ============================================
  // KEY FEATURES — grouped for consolidated display
  // ============================================
  const infrastructureItems = [
    property.borehole && { icon: Droplets, label: 'Borehole' },
    property.mainsWater && { icon: Droplets, label: 'Mains Water' },
    property.waterStorageLitres && { icon: Droplets, label: `Water Tank (${property.waterStorageLitres >= 1000 ? `${(property.waterStorageLitres / 1000).toFixed(0)}K` : property.waterStorageLitres}L)` },
    property.backupGenerator && { icon: Zap, label: 'Generator' },
    property.solar && { icon: Sun, label: 'Solar Power' },
    property.fiberInternet && { icon: Wifi, label: 'Fiber Internet' },
  ].filter(Boolean) as { icon: LucideIcon; label: string }[];

  const securityItems = [
    property.gatedCommunity && { icon: Shield, label: 'Gated Community' },
    property.electricFence && { icon: Zap, label: 'Electric Fence' },
    property.cctv && { icon: Camera, label: 'CCTV' },
    property.securityGuards && { icon: UserCheck, label: '24hr Guards' },
    property.alarmSystem && { icon: Bell, label: 'Alarm System' },
    // Legacy fallback: show generic "Security" ONLY if no specific security fields are true
    (!property.gatedCommunity && !property.electricFence && !property.cctv &&
     !property.securityGuards && !property.alarmSystem && property.security) &&
      { icon: Shield, label: 'Security' },
  ].filter(Boolean) as { icon: LucideIcon; label: string }[];

  const amenityItems = [
    property.pool && { icon: Waves, label: 'Swimming Pool' },
    property.gym && { icon: Dumbbell, label: 'Gym' },
    property.lift && { icon: ArrowUpDown, label: 'Lift / Elevator' },
    property.enSuite && { icon: Bath, label: 'En-Suite' },
    property.garden && { icon: TreePine, label: 'Garden' },
    property.kidsPlayArea && { icon: Baby, label: 'Kids Play Area' },
    property.airCon && { icon: Wind, label: 'Air Conditioning' },
    property.balcony && { icon: Sun, label: 'Balcony' },
    property.furnished && { icon: Armchair, label: 'Furnished' },
    property.fireplace && { icon: Flame, label: 'Fireplace' },
    property.staffQuarters && { icon: Users, label: 'Staff Quarters / DSQ' },
    property.garage && { icon: Car, label: 'Garage' },
    property.nearTarmac && { icon: Route, label: 'Near Tarmac Road' },
  ].filter(Boolean) as { icon: LucideIcon; label: string }[];

  const hasAnyFeatures = infrastructureItems.length > 0 || securityItems.length > 0 || amenityItems.length > 0;

  // =============================================
  // CLIENT STATE (hydration)
  // =============================================

  // Image gallery (mobile hero carousel)
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  const minSwipeDistance = 50;

  // Lightbox state (unified full-screen image viewer)
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const openLightbox = useCallback((index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  }, []);

  // Save
  const [isSaved, setIsSaved] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);

  // Contact Agent modal + Auth prompt
  const [showContactModal, setShowContactModal] = useState(false);
  const [showAuthPrompt, setShowAuthPrompt] = useState(false);

  // Share
  const [showShareModal, setShowShareModal] = useState(false);

  // Location map
  const [isMapReady, setIsMapReady] = useState(false);
  const locationMapRef = useRef<HTMLDivElement>(null);
  const locationMapInstance = useRef<any>(null);

  // Monthly payment calculator
  const [showCalculator, setShowCalculator] = useState(false);
  const [downPaymentPercent, setDownPaymentPercent] = useState(20);
  const [interestRate, setInterestRate] = useState(13.5);
  const [loanTermYears, setLoanTermYears] = useState(20);

  // Description progressive disclosure
  const [descExpanded, setDescExpanded] = useState(false);
  const DESC_LIMIT = 200;
  const descriptionText = property.description || '';
  const shouldTruncateDesc = descriptionText.length > DESC_LIMIT;

  // =============================================
  // MONTHLY PAYMENT CALCULATION (SSR-safe, pure math)
  // =============================================
  const monthlyPaymentData = useMemo(() => {
    const price = property.price;
    const downPayment = price * (downPaymentPercent / 100);
    const loanAmount = price - downPayment;
    const monthlyMortgage = calculateMonthlyPayment(loanAmount, interestRate / 100, loanTermYears);
    const serviceCharge = property.serviceCharge || 0;
    const totalMonthly = monthlyMortgage + serviceCharge;
    const totalPayments = loanTermYears * 12;
    const totalMortgagePayment = monthlyMortgage * totalPayments;
    const totalInterest = totalMortgagePayment - loanAmount;

    return {
      price,
      downPayment,
      loanAmount,
      monthlyMortgage,
      serviceCharge,
      totalMonthly,
      totalPayments,
      totalMortgagePayment,
      totalInterest,
    };
  }, [property.price, property.serviceCharge, downPaymentPercent, interestRate, loanTermYears]);

  // =============================================
  // P0: ANALYTICS + RECENTLY VIEWED (on mount)
  // =============================================
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!property?.id) return;

    // Supabase analytics (fire-and-forget)
    trackEvent('view', property.id, property.agentId);

    // GA4 analytics
    trackGA4ViewItem({
      id: property.id,
      title: property.title,
      price: property.price,
      currency: property.currency,
      type: property.propertyType,
      city: property.city,
    });

    // Recently viewed (localStorage)
    addToRecentlyViewed({
      id: property.id,
      title: property.title,
      images: property.images,
      image: property.image,
      price: property.price,
      currency: property.currency,
      city: property.city,
    });
  }, [property?.id]);

  // =============================================
  // P1: SAVE STATE (check auth + load saved)
  // =============================================
  useEffect(() => {
    if (typeof window === 'undefined') return;

    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user?.id) {
          setUserId(user.id);
          setUserEmail(user.email || null);
          const targets = await getSavedTargets(user.id);
          // `null` is a failed request, not an empty list — leave the heart
          // as it is rather than asserting "not saved" on no evidence.
          if (targets) {
            const key = savedTargetKey(savedListing(property.id));
            setIsSaved(targets.some(t => savedTargetKey(t) === key));
          }
        }
      } catch {
        // Not logged in or error - silent
      }
    })();
  }, [property.id]);

  // =============================================
  // P2: LOCATION MAP (lazy-load Leaflet)
  // =============================================
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!property.lat || !property.lng) return;

    loadLeaflet()
      .then(() => {
        setIsMapReady(true);

        // Wait for DOM then initialize
        setTimeout(() => {
          if (!locationMapRef.current || !window.L || locationMapInstance.current) return;

          const L = window.L;

          locationMapInstance.current = L.map(locationMapRef.current, {
            zoomControl: true,
            scrollWheelZoom: false,
            attributionControl: false,
          }).setView([property.lat!, property.lng!], 15);

          L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png', {
            attribution: '&copy; OpenStreetMap contributors &copy; CARTO',
            subdomains: 'abcd',
            maxZoom: 20,
          }).addTo(locationMapInstance.current);

          // Custom SVG marker (no external images needed)
          const customIcon = L.divIcon({
            className: '',
            html: `
              <svg width="32" height="42" viewBox="0 0 32 42" fill="none" xmlns="http://www.w3.org/2000/svg" style="filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));">
                <path d="M16 0C7.163 0 0 7.163 0 16c0 12 16 26 16 26s16-14 16-26C32 7.163 24.837 0 16 0z" fill="#16A34A"/>
                <circle cx="16" cy="16" r="6" fill="white"/>
              </svg>
            `,
            iconSize: [32, 42],
            iconAnchor: [16, 42],
          });
          const marker = L.marker([property.lat!, property.lng!], { icon: customIcon }).addTo(locationMapInstance.current);

          marker.bindPopup(`
            <div style="padding: 4px;">
              <strong>${property.title}</strong><br/>
              <span style="color: #374151; font-size: 12px;">${property.city}</span>
            </div>
          `).openPopup();
        }, 100);
      })
      .catch(err => console.error('Failed to load map:', err));

    return () => {
      if (locationMapInstance.current) {
        locationMapInstance.current.remove();
        locationMapInstance.current = null;
      }
    };
  }, [property.id, property.lat, property.lng]);

  // =============================================
  // P1: SAVE TOGGLE
  // =============================================
  const handleSaveToggle = useCallback(async () => {
    if (!userId) {
      // Not logged in - show auth prompt (same UX as contact button)
      localStorage.setItem('redirect_after_auth', `/property/${property.slug || property.id}`);
      setShowAuthPrompt(true);
      return;
    }

    // Optimistic update
    const wasSaved = isSaved;
    setIsSaved(!wasSaved);

    try {
      if (wasSaved) {
        // SYND-15: the result is READ now. These functions report failure by
        // returning { success: false } — no token, or any PostgREST error that
        // is not a duplicate — and apiPost/apiDelete swallow network
        // exceptions themselves, so the catch below never fires for them. The
        // heart used to stay in its optimistic state on every one of those,
        // telling the buyer a save had happened that had not.
        const { success } = await unsaveProperty(userId, savedListing(property.id));
        if (!success) {
          setIsSaved(wasSaved);
          return;
        }
      } else {
        const { success } = await saveProperty(userId, savedListing(property.id));
        if (!success) {
          setIsSaved(wasSaved);
          return;
        }
        trackEvent('save', property.id, property.agentId, userId);

        // GA4: add_to_wishlist (only when saving, not unsaving)
        sendGaEvent('add_to_wishlist', {
          currency: property.currency || 'KES',
          value: property.price || 0,
          items: [{
            item_id: property.id,
            item_name: property.title || property.address,
            price: property.price || 0,
          }],
        });
      }
    } catch {
      // Revert on error
      setIsSaved(wasSaved);
    }
  }, [userId, isSaved, property.id, property.slug, property.agentId]);

  // =============================================
  // P1: IMAGE GALLERY SWIPE
  // =============================================
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(() => {
    const distance = touchStartX.current - touchEndX.current;

    if (Math.abs(distance) > minSwipeDistance) {
      if (distance > 0 && currentImageIndex < images.length - 1) {
        setCurrentImageIndex(prev => prev + 1);
      } else if (distance < 0 && currentImageIndex > 0) {
        setCurrentImageIndex(prev => prev - 1);
      }
    }
  }, [currentImageIndex, images.length]);

  const nextImage = useCallback(() => {
    if (currentImageIndex < images.length - 1) {
      setCurrentImageIndex(prev => prev + 1);
    }
  }, [currentImageIndex, images.length]);

  const prevImage = useCallback(() => {
    if (currentImageIndex > 0) {
      setCurrentImageIndex(prev => prev - 1);
    }
  }, [currentImageIndex]);

  // =============================================
  // BACK NAVIGATION
  // =============================================
  // FEED-02 (Vike parity): this renderer needs its own back affordance. The SPA
  // renderer decides this from react-router's `location.key`, which does not
  // exist here — this page is deliberately router-free — so the same question,
  // "is there an in-app entry to return to?", is answered from how the document
  // was entered:
  //   - `isHydration === false` only when Vike client-routed here from another
  //     in-app page, which means it pushed an entry we can return to;
  //   - otherwise this was a document load, and a same-origin referrer is what
  //     separates an in-app click from a deep link, a shared listing URL or a
  //     search result.
  // Both describe how the page was entered, not how it re-renders, so they are
  // read once on mount. Anything we cannot place in-app falls back to the feed
  // rather than sending the user off the site.
  const pageContext = usePageContext();
  const { isHydration } = pageContext as { isHydration?: boolean };
  const [canGoBack, setCanGoBack] = useState(false);

  useEffect(() => {
    if (window.history.length <= 1) return;
    const cameFromSite = document.referrer.startsWith(`${window.location.origin}/`);
    setCanGoBack(cameFromSite || isHydration === false);
  }, [isHydration]);

  const handleBack = useCallback(() => {
    if (canGoBack) {
      window.history.back();
    } else {
      window.location.assign('/');
    }
  }, [canGoBack]);

  // =============================================
  // P1: SHARE
  // =============================================
  const handleShare = useCallback(() => {
    // Try native share API first (mobile)
    if (typeof navigator !== 'undefined' && navigator.share) {
      navigator.share({
        title: property.title,
        text: `Check out this property on Afriqahome: ${property.title}`,
        url: window.location.href,
      }).catch(() => {
        // Fallback to modal if share was cancelled
        setShowShareModal(true);
      });
    } else {
      setShowShareModal(true);
    }

    trackEvent('share', property.id, property.agentId, userId || undefined);

    // GA4: share event
    sendGaEvent('share', {
      method: typeof navigator !== 'undefined' && navigator.share ? 'web_share_api' : 'share_modal',
      content_type: 'property',
      item_id: property.id,
    });
  }, [property, userId]);

  // =============================================
  // P2: CONTACT AGENT (WhatsApp + tel)
  // =============================================
  const whatsappUrl = `https://wa.me/${property.agent.phone?.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(
    `Hi, I'm interested in the property at ${property.title}. Is it still available?`
  )}`;

  const handleContactClick = useCallback(() => {
    // Require auth before opening contact modal (leads need buyer info)
    if (!userId) {
      localStorage.setItem('redirect_after_auth', `/property/${property.slug || property.id}`);
      setShowAuthPrompt(true);
      return;
    }

    // Track analytics
    trackEvent('contact_click', property.id, property.agentId, userId);

    // GA4-06 — INTENT, NOT A LEAD, AND THAT IS THE WHOLE ITEM.
    //
    // This used to send `generate_lead` — a GA4 key event — on the click, before
    // anything was persisted. Every abandoned modal counted as a lead, and every
    // real contact (a conversation with a message in it) counted as nothing,
    // because no event was fired where the write actually lands.
    //
    // `generate_lead` now fires only on confirmed persistence, at the two places
    // that can see a success response: components/chat/ConversationThread.tsx
    // (first message in a new conversation) and the two contact forms. What
    // happens here is the buyer opening a contact surface, which is
    // `contact_start` and nothing more.
    //
    // ONE `contact_start` PER JOURNEY, AND IT IS FIRED HERE. The modal redirects
    // to /messages/<id>, so the buyer crosses two surfaces (modal, then the
    // thread composer) on one contact. Firing at both would restore the
    // double-count in a new place. This is the entry point, so this is the one.
    //
    // It is safe against the navigation that follows: the modal's effect first
    // awaits `isChatCtaEnabled()` and then `findOrCreateConversation()` — two
    // network round-trips — before it sets `window.location.href`, and gtag
    // dispatches via `navigator.sendBeacon`, which survives unload regardless.
    sendGaEvent('contact_start', {
      contact_method: 'chat',
      listing_id: property.id,
    });

    setShowContactModal(true);
  }, [property.id, property.agentId, property.slug, userId]);

  // =============================================
  // ASSIST-01: a guest's "Register interest" — the same prompt as contact.
  // The return path carries ?assist=join; back here, signed in, the card
  // finishes the registration once.
  // =============================================
  const handleAssistSignIn = useCallback((returnTo: string) => {
    rememberReturnAfterAuth(returnTo);
    setShowAuthPrompt(true);
  }, []);

  // The listing as the Assist card sees it: raw column values (R17).
  const assistListing = useMemo(() => ({
    id: property.id,
    source: 'listing' as const,
    propertyType: property.propertyTypeRaw,
    city: property.city,
    status: property.listingStatus,
    agentId: property.agentId,
  }), [property.id, property.propertyTypeRaw, property.city, property.listingStatus, property.agentId]);
  const assistListingPath = `/property/${property.slug || property.id}`;

  // Build canonical URL for sharing
  const propertyUrl = typeof window !== 'undefined'
    ? window.location.href
    : `https://www.afriqahome.com/property/${property.slug}`;

  return (
    <div className="min-h-screen pb-24 md:pb-20 bg-white pt-16">

      {/* =============================================
          HEADER – SSR-safe Navbar
          ============================================= */}
      <HeaderSSR showCurrencyToggle />

      {/* =============================================
          MOBILE HERO - Swipeable Image Carousel
          ============================================= */}
      <div className="md:hidden relative h-[56vh] w-full">
        <div
          className="absolute inset-0 overflow-hidden"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div
            className="flex h-full transition-transform duration-300 ease-out"
            style={{ transform: `translateX(-${currentImageIndex * 100}%)` }}
          >
            {images.length > 0 ? images.map((img, idx) => (
              <div key={idx} className="w-full h-full flex-shrink-0">
                <img
                  src={img}
                  alt={`${property.title} - Image ${idx + 1}`}
                  className="w-full h-full object-cover"
                  loading={idx === 0 ? 'eager' : 'lazy'}
                  onClick={() => openLightbox(idx)}
                />
              </div>
            )) : (
              <div className="w-full h-full flex-shrink-0 bg-gray-100 flex items-center justify-center">
                <div className="text-center text-gray-400">
                  <Home size={48} className="mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No images available</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Top Gradient Overlay */}
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-black/60 via-black/30 to-transparent pointer-events-none z-[5]" />

        {/* Bottom Gradient Overlay */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-black/50 to-transparent pointer-events-none z-[5]" />

        {/* Navigation Buttons — Back (top-left) / Share, Save (top-right).
            FEED-02: the back chip is here for the same reason it was restored in
            the SPA renderer — the navbar logo returns home, and home is not back:
            a user who tapped in from the feed wants the feed they were scrolling,
            not the top of the homepage. This container sits inside the md:hidden
            hero, so the chip is mobile only; the desktop "Back to listings" link
            further down is unchanged. */}
        <div className="absolute top-0 left-0 right-0 pt-[env(safe-area-inset-top)] px-4 py-4 flex justify-between items-start z-10">
          <button
            onClick={handleBack}
            className="p-3 bg-black/30 backdrop-blur-md rounded-full text-white hover:bg-black/40 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
            title="Go back"
            aria-label="Go back"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex space-x-2">
            <button
              onClick={handleShare}
              className="p-3 bg-black/30 backdrop-blur-md rounded-full text-white hover:bg-black/40 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              aria-label="Share"
            >
              <Share size={20} />
            </button>
            <button
              onClick={handleSaveToggle}
              className={`p-3 backdrop-blur-md rounded-full transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center ${
                isSaved ? 'bg-red-500 text-white' : 'bg-black/30 text-white hover:bg-black/40'
              }`}
              aria-label={isSaved ? 'Unsave' : 'Save'}
            >
              <Heart size={20} fill={isSaved ? 'currentColor' : 'none'} />
            </button>
          </div>
        </div>

        {/* Image Counter */}
        {images.length > 1 && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-md text-white px-4 py-2 rounded-full text-sm font-semibold z-10">
            {currentImageIndex + 1} / {images.length}
          </div>
        )}

        {/* Open House Banner - Mobile */}
        {property.nextOpenHouse && (
          <div className="absolute top-20 left-1/2 -translate-x-1/2 z-20 px-4 py-2.5 rounded-2xl shadow-xl flex items-center gap-2 bg-white/95 backdrop-blur-md text-gray-900">
            <Calendar size={18} className="text-green-600" />
            <div>
              <p className="text-[10px] font-medium opacity-80">Open House</p>
              <p className="font-bold text-sm">{property.nextOpenHouse}</p>
            </div>
          </div>
        )}
      </div>

      {/* =============================================
          DESKTOP GALLERY - Hemnet-style Grid
          ============================================= */}
      <div className="hidden md:block max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        {/* Back button and actions row */}
        <div className="flex justify-between items-center mb-4">
          <a
            href="/"
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft size={20} />
            <span className="font-medium">Back to listings</span>
          </a>
          <div className="flex items-center gap-3">
            <button
              onClick={handleShare}
              className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors text-gray-700"
              aria-label="Share"
            >
              <Share size={18} />
              <span className="font-medium">Share</span>
            </button>
            <button
              onClick={handleSaveToggle}
              className={`flex items-center gap-2 px-4 py-2 border rounded-xl transition-colors ${
                isSaved
                  ? 'border-red-200 bg-red-50 text-red-600 hover:bg-red-100'
                  : 'border-gray-200 hover:bg-gray-50 text-gray-700'
              }`}
              aria-label={isSaved ? 'Unsave' : 'Save'}
            >
              <Heart size={18} fill={isSaved ? 'currentColor' : 'none'} />
              <span className="font-medium">{isSaved ? 'Saved' : 'Save'}</span>
            </button>
          </div>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-4 grid-rows-2 gap-2 h-[420px] rounded-2xl overflow-hidden">
          {/* Main Large Image */}
          <div
            className="col-span-2 row-span-2 relative group cursor-pointer"
            onClick={() => openLightbox(0)}
          >
            {images[0] ? (
              <>
                <img
                  src={images[0]}
                  alt={property.title}
                  className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
              </>
            ) : (
              <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                <div className="text-center text-gray-400">
                  <Home size={48} className="mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No images available</p>
                </div>
              </div>
            )}

            {/* Open House Banner - Desktop */}
            {property.nextOpenHouse && (
              <div className="absolute top-4 left-4 z-20 px-4 py-2 rounded-xl shadow-lg flex items-center gap-2 bg-white/95 backdrop-blur-md text-gray-900">
                <Calendar size={18} className="text-green-600" />
                <div>
                  <p className="text-xs font-medium opacity-80">Open House</p>
                  <p className="font-bold">{property.nextOpenHouse}</p>
                </div>
              </div>
            )}
          </div>

          {/* Thumbnail Grid (4 smaller images) */}
          {[1, 2, 3, 4].map((idx) => (
            <div
              key={idx}
              className="relative group cursor-pointer overflow-hidden"
              onClick={() => openLightbox(idx)}
            >
              {images[idx] ? (
                <>
                  <img
                    src={images[idx]}
                    alt={`${property.title} - Photo ${idx + 1}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />

                  {/* "+X more" overlay on last thumbnail */}
                  {idx === 4 && images.length > 5 && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <div className="text-white text-center">
                        <Grid3X3 size={24} className="mx-auto mb-1" />
                        <span className="font-bold">+{images.length - 5} more</span>
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                  <Grid3X3 size={24} className="text-gray-400" />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* View All Photos */}
        {images.length > 1 && (
          <button
            onClick={() => openLightbox(0)}
            className="mt-4 flex items-center gap-2 text-gray-700 hover:text-gray-900 font-medium transition-colors"
          >
            <Grid3X3 size={18} />
            View all {images.length} photos
          </button>
        )}
      </div>

      {/* =============================================
          CONTENT CONTAINER
          ============================================= */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6 md:mt-8">
        {/* Mobile Breadcrumbs — Home › {hub} › {listing}, the same trail the
            BreadcrumbList carries (Brain review of PR #730, R4). The hub is the
            listing's city hub when indexable, else its Kenya-wide type hub,
            else no rung. It replaces the two /?location= search links, which
            robots.txt disallows and no BreadcrumbList ever named. */}
        <nav aria-label="Breadcrumb" className="md:hidden mb-4">
          <ol className="flex items-center gap-2 text-sm text-gray-600 flex-wrap">
            <li>
              <a href="/" className="hover:text-blue-600 transition flex items-center gap-1">
                <Home className="h-4 w-4" />
                <span className="sr-only sm:not-sr-only">Home</span>
              </a>
            </li>
            {property.hubCrumb && (
              <li className="flex items-center gap-2">
                <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
                <a href={property.hubCrumb.href} className="hover:text-blue-600 transition">
                  {property.hubCrumb.name}
                </a>
              </li>
            )}
            <li className="flex items-center gap-2">
              <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
              <span className="text-gray-900 font-medium truncate max-w-[200px]">
                {property.title}
              </span>
            </li>
          </ol>
        </nav>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* =============================================
              MAIN CONTENT (Left Column)
              ============================================= */}
          <div className="lg:w-2/3">

            {/* Header Section */}
            <div className="mb-6">
              <div className={`inline-block px-3 py-1 rounded-full text-xs font-semibold tracking-wide mb-3 ${
                isProject ? 'bg-amber-100 text-gray-900' : 'bg-green-50 text-gray-900'
              }`}>
                {formatPropertyType(property.propertyType)}
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2 leading-tight">
                {property.title}
              </h1>
              <p className="text-base text-gray-500 flex items-center">
                <MapPin size={18} className="mr-2 shrink-0 text-gray-400" />
                {property.neighborhood && property.neighborhood !== property.city
                  ? `${property.neighborhood}, ${property.city}`
                  : property.city}
              </p>
            </div>

            {/* =============================================
                PRICE BAR WITH INLINE SPECS
                ============================================= */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between py-4 border-t border-b border-gray-200 mb-6 gap-3">
              {/* CURR-03: three steps, not two.
                  30px bold KES -> 20px medium USD -> 14px muted fine print.
                  The USD figure used to render at the same size and colour as
                  the disclaimer, the price/m² and the listed date, so the one
                  number among four lines of annotation read as a footnote. It
                  now sits between them, and the two-line disclaimer it used to
                  share a weight with is gone — the figure carries its own
                  inline qualifier. KES is untouched and still first.
                  Kept identical to pages/PropertyDetails.tsx: the same property
                  renders through this tree on a direct link and through that
                  one after hydration. */}
              <div>
                <p className="text-3xl font-bold text-gray-900">
                  {isProject ? (
                    <>From {formatListingPrice(property.price, property.currency)}</>
                  ) : (
                    formatListingPrice(property.price, property.currency)
                  )}
                </p>
                <UsdApprox
                  amountKES={property.price}
                  currency={property.currency}
                  variant="detail"
                  className="mt-1"
                />
                <p className="text-sm text-gray-500 mt-1">
                  {pricePerSqm && property.area > 0 && (
                    <>KES {formatKesAmount(pricePerSqm)}/m² <span className="mx-1.5">·</span></>
                  )}
                  Listed {Math.max(1, daysOnMarket || 1)} {(daysOnMarket || 1) === 1 ? 'day' : 'days'} ago
                </p>
              </div>
              <div className="flex items-center gap-5 text-gray-700">
                <span className="flex items-center gap-1.5">
                  <Bed size={18} className="text-gray-400" />
                  <span className="font-semibold">
                    {property.bedsMax && property.bedsMax > property.beds
                      ? `${property.beds}–${property.bedsMax}`
                      : property.beds || '—'}
                  </span>
                  <span className="text-gray-500 text-sm">Beds</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <Bath size={18} className="text-gray-400" />
                  <span className="font-semibold">
                    {property.bathsMax && property.bathsMax > property.baths
                      ? `${property.baths}–${property.bathsMax}`
                      : property.baths || '—'}
                  </span>
                  <span className="text-gray-500 text-sm">Baths</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <Maximize size={18} className="text-gray-400" />
                  <span className="font-semibold">{property.area || '—'}</span>
                  <span className="text-gray-500 text-sm">m²</span>
                </span>
              </div>
            </div>

            {/* =============================================
                TRUST BANNER — Trust-first for diaspora buyers
                ============================================= */}
            {(() => {
              const trustItems: { label: string }[] = [];

              if (property.titleDeedType) {
                const deedLabel = property.titleDeedType === 'freehold' ? 'Freehold' :
                  property.titleDeedType === 'leasehold'
                    ? `Leasehold${property.leaseholdYearsRemaining ? ` (${property.leaseholdYearsRemaining} yrs)` : ''}`
                    : 'Sectional Title';
                trustItems.push({ label: deedLabel });
              }
              if (property.titleReady) trustItems.push({ label: 'Title Ready' });
              if (property.encumbranceFree) trustItems.push({ label: 'Encumbrance Free' });

              if (trustItems.length === 0) return null;

              return (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-3.5 mb-6">
                  <div className="flex flex-wrap gap-x-5 gap-y-2 items-center">
                    {trustItems.map((item, i) => (
                      <div key={i} className="flex items-center gap-1.5 text-sm font-medium text-emerald-800">
                        <CheckCircle2 size={16} className="text-emerald-600 shrink-0" />
                        <span>{item.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* =============================================
                FACTS GRID — Clean icon-based layout
                ============================================= */}
            <section className="mb-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Facts &amp; Specifications</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {/* Bedrooms */}
                <div className="bg-gray-50 rounded-xl p-4 flex flex-col items-center text-center">
                  <Bed size={20} className="text-gray-400 mb-1.5" />
                  <span className="text-xs text-gray-500 mb-0.5">Beds</span>
                  <span className="text-base font-bold text-gray-900">
                    {property.bedsMax && property.bedsMax > property.beds
                      ? `${property.beds}–${property.bedsMax}`
                      : property.beds || '—'}
                  </span>
                </div>

                {/* Bathrooms */}
                <div className="bg-gray-50 rounded-xl p-4 flex flex-col items-center text-center">
                  <Bath size={20} className="text-gray-400 mb-1.5" />
                  <span className="text-xs text-gray-500 mb-0.5">Baths</span>
                  <span className="text-base font-bold text-gray-900">
                    {property.bathsMax && property.bathsMax > property.baths
                      ? `${property.baths}–${property.bathsMax}`
                      : property.baths || '—'}
                  </span>
                </div>

                {/* Area */}
                <div className="bg-gray-50 rounded-xl p-4 flex flex-col items-center text-center">
                  <Maximize size={20} className="text-gray-400 mb-1.5" />
                  <span className="text-xs text-gray-500 mb-0.5">Area</span>
                  <span className="text-base font-bold text-gray-900">
                    {property.area ? `${property.area} m²` : '—'}
                  </span>
                </div>

                {/* Property Type */}
                <div className="bg-gray-50 rounded-xl p-4 flex flex-col items-center text-center">
                  <Home size={20} className="text-gray-400 mb-1.5" />
                  <span className="text-xs text-gray-500 mb-0.5">Type</span>
                  <span className="text-base font-bold text-gray-900">
                    {formatPropertyType(property.propertyType)}
                  </span>
                </div>

                {/* Parking Spaces - only if parkingSpaces > 0 */}
                {property.parkingSpaces != null && property.parkingSpaces > 0 && (
                  <div className="bg-gray-50 rounded-xl p-4 flex flex-col items-center text-center">
                    <Car size={20} className="text-gray-400 mb-1.5" />
                    <span className="text-xs text-gray-500 mb-0.5">Parking</span>
                    <span className="text-base font-bold text-gray-900">
                      {property.parkingSpaces} space{property.parkingSpaces > 1 ? 's' : ''}
                    </span>
                  </div>
                )}

                {/* Floor Level - only for apartments/townhouses with floorLevel */}
                {property.floorLevel != null &&
                 ['apartment', 'townhouse'].includes(property.propertyType?.toLowerCase()) && (
                  <div className="bg-gray-50 rounded-xl p-4 flex flex-col items-center text-center">
                    <Building2 size={20} className="text-gray-400 mb-1.5" />
                    <span className="text-xs text-gray-500 mb-0.5">Floor</span>
                    <span className="text-base font-bold text-gray-900">
                      {formatFloorLevel(property.floorLevel)}
                    </span>
                  </div>
                )}

                {/* Price per m² - only if area > 0 */}
                {property.area > 0 && pricePerSqm && (
                  <div className="bg-gray-50 rounded-xl p-4 flex flex-col items-center text-center">
                    <TrendingUp size={20} className="text-gray-400 mb-1.5" />
                    <span className="text-xs text-gray-500 mb-0.5">Price/m²</span>
                    <span className="text-base font-bold text-gray-900">
                      KES {formatKesAmount(pricePerSqm)}
                    </span>
                  </div>
                )}

                {/* Service Charge */}
                {property.serviceCharge && property.serviceCharge > 0 && (
                  <div className="bg-gray-50 rounded-xl p-4 flex flex-col items-center text-center">
                    <Banknote size={20} className="text-gray-400 mb-1.5" />
                    <span className="text-xs text-gray-500 mb-0.5">Service Charge</span>
                    <span className="text-base font-bold text-gray-900">
                      KES {formatKesAmount(property.serviceCharge)}/mo
                    </span>
                  </div>
                )}
              </div>
            </section>

            {/* =============================================
                ABOUT THE HOME — Progressive Disclosure
                ============================================= */}
            {descriptionText && (
              <section className="mb-8">
                <h2 className="text-lg font-semibold text-gray-900 mb-3">About the Home</h2>
                <div className="relative">
                  <div
                    className={`text-gray-600 leading-relaxed whitespace-pre-line transition-all duration-200 ease-out print:max-h-none print:overflow-visible ${
                      !descExpanded && shouldTruncateDesc ? 'max-h-24 overflow-hidden' : ''
                    }`}
                  >
                    {descriptionText}
                  </div>
                  {/* Fade gradient overlay when truncated */}
                  {!descExpanded && shouldTruncateDesc && (
                    <div className="absolute bottom-0 inset-x-0 h-12 bg-gradient-to-t from-white to-transparent pointer-events-none print:hidden" />
                  )}
                </div>
                {shouldTruncateDesc && (
                  <button
                    onClick={() => setDescExpanded(!descExpanded)}
                    className="mt-2 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors flex items-center gap-1 min-h-[44px] print:hidden"
                  >
                    {descExpanded ? 'Show less' : 'Show more'}
                    <ChevronDown
                      size={14}
                      className={`transition-transform duration-200 ${descExpanded ? 'rotate-180' : ''}`}
                    />
                  </button>
                )}
              </section>
            )}

            {/* =============================================
                KEY FEATURES — Consolidated
                ============================================= */}
            {hasAnyFeatures && (
              <section className="mb-8">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Key Features</h2>
                <div className="bg-white border border-gray-100 rounded-2xl p-5 space-y-5">

                  {/* Infrastructure */}
                  {infrastructureItems.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2.5">
                        Infrastructure
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {infrastructureItems.map((item, i) => (
                          <div key={i} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg text-sm text-gray-700">
                            <item.icon size={16} className="text-gray-500 shrink-0" />
                            <span>{item.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Divider */}
                  {infrastructureItems.length > 0 && securityItems.length > 0 && (
                    <hr className="border-gray-100" />
                  )}

                  {/* Security */}
                  {securityItems.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2.5">
                        Security
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {securityItems.map((item, i) => (
                          <div key={i} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg text-sm text-gray-700">
                            <item.icon size={16} className="text-gray-500 shrink-0" />
                            <span>{item.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Divider */}
                  {(infrastructureItems.length > 0 || securityItems.length > 0) && amenityItems.length > 0 && (
                    <hr className="border-gray-100" />
                  )}

                  {/* Amenities */}
                  {amenityItems.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2.5">
                        Amenities
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {amenityItems.map((item, i) => (
                          <div key={i} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg text-sm text-gray-700">
                            <item.icon size={16} className="text-gray-500 shrink-0" />
                            <span>{item.label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                </div>
              </section>
            )}

            {/* =============================================
                AVAILABLE UNITS - Only for New Development / project listings
                ============================================= */}
            {isProject && property.units && property.units.length > 0 && (
              <section className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
                    <Building2 size={20} className="text-emerald-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Available Units</h2>
                    <p className="text-sm text-gray-500">
                      {property.units.length} unit type{property.units.length !== 1 ? 's' : ''} available
                    </p>
                  </div>
                </div>

                {/* Scrollable table wrapper for mobile */}
                <div className="border border-gray-200 rounded-2xl overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[700px]">
                      <thead>
                        <tr className="bg-gray-50 border-b border-gray-200">
                          <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Unit</th>
                          <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Floor</th>
                          <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Layout</th>
                          <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Size</th>
                          <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Qty</th>
                          <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Status</th>
                          <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">Price</th>
                          <th className="px-4 py-3"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {property.units.map((unit: any, index: number) => {
                          const status = (unit.status || 'available').toLowerCase();
                          const isSold = status === 'sold';
                          const isReserved = status === 'reserved';
                          const isAvailable = !isSold && !isReserved;
                          const isLowStock = unit.units_available !== undefined && unit.units_available < 3 && unit.units_available > 0;

                          return (
                            <tr
                              key={unit.id || index}
                              className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors"
                            >
                              <td className="px-4 py-3.5 text-sm font-medium text-gray-900 whitespace-nowrap">
                                {unit.unit_number || unit.unitNumber || `Unit ${index + 1}`}
                              </td>
                              <td className="px-4 py-3.5 text-sm text-gray-600">
                                {unit.floor || '—'}
                              </td>
                              <td className="px-4 py-3.5 text-sm text-gray-600 whitespace-nowrap">
                                {unit.bedrooms != null ? `${unit.bedrooms} Bed` : ''}
                                {unit.bedrooms != null && unit.bathrooms != null ? ', ' : ''}
                                {unit.bathrooms != null ? `${unit.bathrooms} Bath` : ''}
                              </td>
                              <td className="px-4 py-3.5 text-sm text-gray-600 whitespace-nowrap">
                                {unit.size ? `${unit.size} m²` : '—'}
                              </td>
                              <td className="px-4 py-3.5 text-sm font-medium text-gray-900">
                                {unit.units_available || 1}
                              </td>
                              <td className="px-4 py-3.5">
                                {isSold ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                                    Sold
                                  </span>
                                ) : isReserved ? (
                                  <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                                    <Flame size={12} className="fill-amber-500"/>
                                    Reserved
                                  </span>
                                ) : isLowStock ? (
                                  <span className="flex items-center text-amber-600 font-bold text-xs gap-1.5">
                                    <Flame size={14} className="fill-amber-500"/>
                                    Only {unit.units_available} left!
                                  </span>
                                ) : (
                                  <span className="flex items-center text-green-600 font-bold text-xs gap-1.5">
                                    <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                                    Available
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3.5 text-right">
                                <span className="text-sm font-bold text-gray-900">
                                  {unit.price
                                    ? formatListingPrice(unit.price, property.currency)
                                    : '—'}
                                  {unit.price ? (
                                    <UsdApprox
                                      amountKES={unit.price}
                                      currency={property.currency}
                                    />
                                  ) : null}
                                </span>
                              </td>
                              <td className="px-4 py-3.5">
                                {isAvailable && (
                                  <button
                                    onClick={handleContactClick}
                                    className="px-3 py-1.5 bg-emerald-600 text-white text-xs font-semibold rounded-lg hover:bg-emerald-700 active:bg-emerald-800 transition-colors whitespace-nowrap"
                                  >
                                    Inquire
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      {/* Total row */}
                      <tfoot>
                        <tr className="border-t-2 border-gray-200 bg-gray-50">
                          <td colSpan={4} className="px-4 py-3 text-sm font-semibold text-gray-700">
                            Total Units
                          </td>
                          <td className="px-4 py-3 text-sm font-bold text-gray-900">
                            {property.units.reduce((sum: number, unit: any) => sum + (unit.units_available || 1), 0)}
                          </td>
                          <td colSpan={3}></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                {/* Contact Sales Team CTA */}
                <div className="mt-4">
                  <button
                    onClick={handleContactClick}
                    className="w-full py-3.5 bg-emerald-600 text-white rounded-xl font-semibold text-sm hover:bg-emerald-700 active:bg-emerald-800 transition-colors flex items-center justify-center gap-2"
                  >
                    <MessageCircle size={18} />
                    Contact Sales Team
                  </button>
                </div>
              </section>
            )}

            {/* =============================================
                LAND DETAILS - Only for land/plot property types
                ============================================= */}
            {(() => {
              const p = property as any;
              const isLand = property.propertyType?.toLowerCase() === 'land' || property.propertyType?.toLowerCase() === 'plot';

              if (!isLand) return null;

              const roadAccess = formatRoadAccess(p.roadAccessType);
              const zoning = formatZoning(p.zoning);
              const distanceFromTarmac = p.distanceFromTarmacM && p.roadAccessType !== 'tarmac'
                ? `${Number(p.distanceFromTarmacM).toLocaleString()} meters from tarmac road`
                : null;
              const beaconed = p.beaconed;
              const surveyApproved = p.surveyApproved;

              const hasLandData = roadAccess || zoning || distanceFromTarmac || beaconed || surveyApproved;

              if (!hasLandData) return null;

              return (
                <section className="mb-8 bg-white rounded-2xl p-5 border border-gray-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <MapPinned size={20} className="text-orange-700" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900">Land Details</h2>
                      <p className="text-xs text-gray-500">Plot-specific information</p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {roadAccess && (
                      <div className="flex items-center gap-3">
                        <Route size={18} className="text-orange-600" />
                        <div>
                          <span className="text-sm text-gray-600">Road Access:</span>
                          <span className="ml-2 font-semibold text-gray-900">{roadAccess}</span>
                        </div>
                      </div>
                    )}
                    {distanceFromTarmac && (
                      <div className="flex items-center gap-3">
                        <Route size={18} className="text-orange-600" />
                        <div>
                          <span className="text-sm text-gray-600">Distance:</span>
                          <span className="ml-2 font-semibold text-gray-900">{distanceFromTarmac}</span>
                        </div>
                      </div>
                    )}
                    {zoning && (
                      <div className="flex items-center gap-3">
                        <Building size={18} className="text-orange-600" />
                        <div>
                          <span className="text-sm text-gray-600">Zoning:</span>
                          <span className="ml-2 font-semibold text-gray-900">{zoning}</span>
                        </div>
                      </div>
                    )}
                    {beaconed && (
                      <div className="flex items-center gap-3">
                        <MapPinned size={18} className="text-green-600" />
                        <span className="text-sm font-semibold text-green-700 flex items-center gap-1">
                          <CheckCircle size={14} /> Survey beacons placed
                        </span>
                      </div>
                    )}
                    {surveyApproved && (
                      <div className="flex items-center gap-3">
                        <FileCheck size={18} className="text-green-600" />
                        <span className="text-sm font-semibold text-green-700 flex items-center gap-1">
                          <CheckCircle size={14} /> Survey officially approved
                        </span>
                      </div>
                    )}
                  </div>
                </section>
              );
            })()}

            {/* =============================================
                LOCATION MAP (Leaflet, lazy-loaded)
                ============================================= */}
            <section className="mb-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Location</h2>
              {property.lat != null && property.lng != null ? (
                <>
                  <p className="text-gray-600 mb-4 flex items-center">
                    <MapPin size={18} className="mr-2 text-gray-400" />
                    {property.neighborhood && property.neighborhood !== property.city
                      ? `${property.neighborhood}, ${property.city}`
                      : property.city}
                  </p>

                  {/* Interactive Leaflet Map */}
                  <div className="rounded-2xl overflow-hidden border border-gray-200 relative">
                    <div
                      ref={locationMapRef}
                      className="h-[300px] w-full z-0"
                      style={{ background: '#f5f5f5' }}
                    />

                    {/* Loading state */}
                    {!isMapReady && (
                      <div className="absolute inset-0 bg-gray-100 rounded-2xl overflow-hidden animate-pulse">
                        {/* Fake map grid lines */}
                        <div className="absolute inset-0 opacity-[0.08]">
                          {/* Horizontal lines */}
                          <div className="absolute top-1/4 left-0 right-0 h-px bg-gray-400" />
                          <div className="absolute top-2/4 left-0 right-0 h-px bg-gray-400" />
                          <div className="absolute top-3/4 left-0 right-0 h-px bg-gray-400" />
                          {/* Vertical lines */}
                          <div className="absolute left-1/4 top-0 bottom-0 w-px bg-gray-400" />
                          <div className="absolute left-2/4 top-0 bottom-0 w-px bg-gray-400" />
                          <div className="absolute left-3/4 top-0 bottom-0 w-px bg-gray-400" />
                        </div>

                        {/* Center pin indicator */}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="flex flex-col items-center gap-2">
                            <div className="w-8 h-8 bg-gray-300 rounded-full" />
                            <div className="w-16 h-3 bg-gray-200 rounded" />
                          </div>
                        </div>

                        {/* Bottom-right "loading" indicator */}
                        <div className="absolute bottom-3 right-3 bg-white/80 backdrop-blur-sm px-3 py-1.5 rounded-lg shadow-sm">
                          <div className="flex items-center gap-2">
                            <Loader2 size={12} className="animate-spin text-gray-400" />
                            <span className="text-xs text-gray-500">Loading map</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Google Maps link overlay */}
                    <a
                      href={`https://www.google.com/maps?q=${property.lat},${property.lng}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="absolute bottom-3 right-3 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-lg shadow-sm text-xs font-medium text-gray-700 hover:bg-white transition-colors flex items-center gap-1.5 z-[400]"
                    >
                      <ExternalLink size={12} />
                      Open in Google Maps
                    </a>
                  </div>
                </>
              ) : (
                /* Map placeholder when coordinates missing */
                <div className="h-[300px] bg-gray-50 rounded-2xl flex items-center justify-center border border-gray-100">
                  <div className="text-center text-gray-400">
                    <MapPin size={32} className="mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Map not available for this listing</p>
                  </div>
                </div>
              )}
            </section>

            {/* =============================================
                MOBILE AGENT CARD — Visible only on mobile (lg:hidden)
                Duplicated from sidebar so mobile users can contact agent
                ============================================= */}
            <section className="mb-8 lg:hidden">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Contact Agent</h2>
              <div className="bg-white border border-gray-200 rounded-2xl p-5">
                {/* Agent info row */}
                <a
                  href={`/agent/${property.agent.slug || property.agent.id}`}
                  className="flex items-center gap-3 mb-4 group"
                >
                  {property.agent.image ? (
                    <img
                      src={property.agent.image}
                      alt={`${property.agent.name} - Afriqahome Agent`}
                      className="w-14 h-14 rounded-full object-cover border-2 border-gray-100 group-hover:border-blue-200 transition-colors"
                    />
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 text-lg font-semibold border-2 border-gray-100">
                      {property.agent.name?.charAt(0) || 'A'}
                    </div>
                  )}
                  <div className="min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate group-hover:text-blue-600 transition-colors">
                      {property.agent.name}
                    </h3>
                    {property.agent.agencyName && (
                      <p className="text-sm text-gray-500 truncate">{property.agent.agencyName}</p>
                    )}
                    {property.agent.isVerified && (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 mt-0.5">
                        <CheckCircle2 size={12} className="shrink-0" /> Verified Agent
                      </span>
                    )}
                  </div>
                </a>

                {/* CTA buttons — stacked vertically */}
                <div className="space-y-2">
                  {/* Primary: Call Agent */}
                  {property.agent.phone && (
                    <a
                      href={`tel:${property.agent.phone}`}
                      className="flex items-center justify-center gap-2 w-full py-3 bg-blue-600 text-white rounded-xl font-semibold text-sm hover:bg-blue-700 active:bg-blue-800 transition-colors min-h-[48px]"
                    >
                      <Phone size={16} /> Call Agent
                    </a>
                  )}

                  {/* Secondary: WhatsApp */}
                  {property.agent.phone && (
                    <a
                      href={whatsappUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full py-3 bg-green-600 text-white rounded-xl font-semibold text-sm hover:bg-green-700 active:bg-green-800 transition-colors min-h-[48px]"
                    >
                      <MessageCircle size={16} /> WhatsApp
                    </a>
                  )}

                  {/* Tertiary: Send Message (in-app) */}
                  <button
                    onClick={handleContactClick}
                    className="flex items-center justify-center gap-2 w-full py-3 border border-gray-300 text-gray-700 rounded-xl font-semibold text-sm hover:bg-gray-50 active:bg-gray-100 transition-colors min-h-[48px]"
                  >
                    <Mail size={16} /> Send Message
                  </button>
                </div>

              </div>
            </section>

            {/* ASSIST-01 (R30): the Assist card, next after the agent card. */}
            <AssistCard
              listing={assistListing}
              listingPath={assistListingPath}
              onSignInRequired={handleAssistSignIn}
              className="mb-8 lg:hidden"
            />

          </div>

          {/* =============================================
              SIDEBAR / AGENT CARD (Desktop only)
              ============================================= */}
          <div className="hidden lg:block lg:w-1/3 space-y-6">

            {/* Open House Card */}
            {property.nextOpenHouse && (
              <div className="rounded-3xl p-6 shadow-sm border bg-white border-gray-100">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-gray-100 text-gray-600">
                    <Calendar size={24} />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900">Open House</h3>
                    <p className="font-semibold text-gray-700">
                      {property.nextOpenHouse}
                    </p>
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-3">
                  Walk-in welcome. No appointment needed.
                </p>
              </div>
            )}

            {/* ASSIST-01 (R30): one sticky wrapper holds the agent card and the
                Assist card, so the Assist card never scrolls under the agent
                card. The agent card moved its `sticky top-24` here and is
                otherwise unchanged (left at its old indentation on purpose). */}
            <div className="sticky top-24 space-y-6">
            {/* =============================================
                AGENT CARD — Conversion-focused
                ============================================= */}
            <div className="bg-white border border-gray-200 rounded-2xl p-5">
              {/* Agent info row */}
              <a
                href={`/agent/${property.agent.slug || property.agent.id}`}
                className="flex items-center gap-3 mb-4 group"
              >
                {property.agent.image ? (
                  <img
                    src={property.agent.image}
                    alt={`${property.agent.name} - Afriqahome Agent`}
                    className="w-14 h-14 rounded-full object-cover border-2 border-gray-100 group-hover:border-blue-200 transition-colors"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 text-lg font-semibold border-2 border-gray-100">
                    {property.agent.name?.charAt(0) || 'A'}
                  </div>
                )}
                <div className="min-w-0">
                  <h3 className="font-semibold text-gray-900 truncate group-hover:text-blue-600 transition-colors">
                    {property.agent.name}
                  </h3>
                  {property.agent.agencyName && (
                    <p className="text-sm text-gray-500 truncate">{property.agent.agencyName}</p>
                  )}
                  {property.agent.isVerified && (
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 mt-0.5">
                      <CheckCircle2 size={12} className="shrink-0" /> Verified Agent
                    </span>
                  )}
                </div>
              </a>

              {/* CTA buttons — stacked vertically, clear hierarchy */}
              <div className="space-y-2">
                {/* Primary: Call Agent */}
                {property.agent.phone && (
                  <a
                    href={`tel:${property.agent.phone}`}
                    className="flex items-center justify-center gap-2 w-full py-3 bg-blue-600 text-white rounded-xl font-semibold text-sm hover:bg-blue-700 active:bg-blue-800 transition-colors min-h-[48px]"
                  >
                    <Phone size={16} /> Call Agent
                  </a>
                )}

                {/* Secondary: WhatsApp */}
                {property.agent.phone && (
                  <a
                    href={whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 w-full py-3 bg-green-600 text-white rounded-xl font-semibold text-sm hover:bg-green-700 active:bg-green-800 transition-colors min-h-[48px]"
                  >
                    <MessageCircle size={16} /> WhatsApp
                  </a>
                )}

                {/* Tertiary: Send Message (in-app) */}
                <button
                  onClick={handleContactClick}
                  className="flex items-center justify-center gap-2 w-full py-3 border border-gray-300 text-gray-700 rounded-xl font-semibold text-sm hover:bg-gray-50 active:bg-gray-100 transition-colors min-h-[48px]"
                >
                  <Mail size={16} /> Send Message
                </button>
              </div>

            </div>

            <AssistCard
              listing={assistListing}
              listingPath={assistListingPath}
              onSignInRequired={handleAssistSignIn}
            />
            </div>

            {/* =============================================
                MONTHLY PAYMENT ESTIMATOR (Sidebar, collapsed)
                ============================================= */}
            {!isProject && (
              <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                <button
                  onClick={() => setShowCalculator(!showCalculator)}
                  className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Calculator size={18} className="text-blue-600" />
                    <div className="text-left">
                      <p className="text-sm font-semibold text-gray-900">Monthly Estimate</p>
                      <p className="text-xs text-gray-500">Calculate affordability</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold text-blue-600">
                      KES {formatKesAmount(monthlyPaymentData.totalMonthly)}
                    </span>
                    <ChevronDown size={16} className={`text-gray-400 transition-transform duration-200 ${showCalculator ? 'rotate-180' : ''}`} />
                  </div>
                </button>

                {showCalculator && (
                  <div className="p-4 border-t border-gray-100 space-y-4">
                    {/* Sliders */}
                    <div className="space-y-4">
                      {/* Down Payment */}
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <label className="text-sm font-medium text-gray-700">Down Payment</label>
                          <span className="text-sm font-bold text-blue-600">{downPaymentPercent}%</span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max="90"
                          step="5"
                          value={downPaymentPercent}
                          onChange={(e) => setDownPaymentPercent(Number(e.target.value))}
                          className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>KES {formatKesAmount(monthlyPaymentData.downPayment)}</span>
                          <span>0-90%</span>
                        </div>
                      </div>

                      {/* Interest Rate */}
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <label className="text-sm font-medium text-gray-700">Interest Rate</label>
                          <span className="text-sm font-bold text-blue-600">{interestRate}%</span>
                        </div>
                        <input
                          type="range"
                          min="0"
                          max="25"
                          step="0.5"
                          value={interestRate}
                          onChange={(e) => setInterestRate(Number(e.target.value))}
                          className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>Kenya avg: ~13.5%</span>
                          <span>0-25%</span>
                        </div>
                      </div>

                      {/* Loan Term */}
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <label className="text-sm font-medium text-gray-700">Loan Term</label>
                          <span className="text-sm font-bold text-blue-600">{loanTermYears} years</span>
                        </div>
                        <input
                          type="range"
                          min="1"
                          max="30"
                          step="1"
                          value={loanTermYears}
                          onChange={(e) => setLoanTermYears(Number(e.target.value))}
                          className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                        />
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>{loanTermYears * 12} payments</span>
                          <span>1-30 years</span>
                        </div>
                      </div>
                    </div>

                    {/* Cost Breakdown */}
                    <div className="bg-gray-50 rounded-xl p-3 space-y-2">
                      <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                        <TrendingDown size={14} className="text-blue-600" />
                        Monthly Breakdown
                      </h4>
                      <div className="space-y-1.5 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Mortgage</span>
                          <span className="font-medium">KES {formatKesAmount(monthlyPaymentData.monthlyMortgage)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Service Charge</span>
                          <span className="font-medium">KES {formatKesAmount(monthlyPaymentData.serviceCharge)}</span>
                        </div>
                        <div className="border-t border-gray-200 pt-1.5 flex justify-between">
                          <span className="font-semibold text-gray-900">Total</span>
                          <span className="font-bold text-blue-600">KES {formatKesAmount(monthlyPaymentData.totalMonthly)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Loan Summary */}
                    <div className="text-xs text-gray-500 space-y-1">
                      <div className="flex justify-between">
                        <span>Loan Amount</span>
                        <span>KES {formatKesAmount(monthlyPaymentData.loanAmount)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Total Interest</span>
                        <span className="text-amber-600">KES {formatKesAmount(monthlyPaymentData.totalInterest)}</span>
                      </div>
                    </div>

                    {/* Disclaimer */}
                    <div className="flex items-start gap-2 text-xs text-gray-500 bg-gray-50 p-2.5 rounded-lg">
                      <Info size={12} className="shrink-0 mt-0.5" />
                      <p>Estimate only. Contact a bank for accurate pre-approval.</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* =============================================
          FOOTER – SSR-safe site footer
          ============================================= */}
      <FooterSSR />

      {/* =============================================
          MOBILE FLOATING FOOTER — Price-first design
          Shows price + specs left, CTAs right
          ============================================= */}
      {/* GROWTH-01: `data-app-banner-anchor` marks this bar as the thing
          MobileAppPrompt must sit ABOVE. It measures the element rather
          than assuming a height, because the UsdApprox line above is
          conditional and a long price wraps. Nothing else about this bar
          changes, and the banner never touches it — dismissing the banner
          leaves this layout exactly as it was. */}
      <div
        data-app-banner-anchor
        className="fixed bottom-0 left-0 right-0 lg:hidden z-40 pb-[env(safe-area-inset-bottom)] print:hidden"
      >
        <div className="mx-3 mb-3 p-3 bg-white/95 backdrop-blur-md rounded-2xl shadow-[0_-4px_30px_rgba(0,0,0,0.12)] border border-gray-200">
          <div className="flex items-center justify-between gap-3">
            {/* Price + Quick Specs
                CURR-02/CURR-03: the approximation reaches the sticky bar here.

                It gets its own line rather than sitting inline beside the KES
                figure. Between the two CTAs, this cell is roughly 110px wide at
                375px — narrower than "KES 12,500,000" already needs — so an
                inline USD would spend width the canonical figure is using and
                push it into the ellipsis. On its own line it competes with
                nothing, and compact rounding keeps it under nine characters, so
                it never truncates either. KES stays first, and stays whole. */}
            <div className="min-w-0 flex-shrink">
              <p className="text-lg font-bold text-gray-900 truncate">
                {formatListingPrice(property.price, property.currency)}
              </p>
              <UsdApprox
                amountKES={property.price}
                currency={property.currency}
                variant="sticky"
              />
              <p className="text-xs text-gray-500 truncate">
                {property.rooms > 0 && `${property.rooms} bed`}
                {property.rooms > 0 && property.bathrooms > 0 && ' · '}
                {property.bathrooms > 0 && `${property.bathrooms} bath`}
                {(property.rooms > 0 || property.bathrooms > 0) && property.area > 0 && ' · '}
                {property.area > 0 && `${property.area.toLocaleString()} m²`}
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex gap-2 flex-shrink-0">
              {/* Call button — only if phone exists */}
              {property.agent.phone && (
                <a
                  href={`tel:${property.agent.phone}`}
                  className="px-4 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold whitespace-nowrap min-h-[44px] flex items-center gap-1.5 active:bg-blue-800 transition-colors"
                >
                  <Phone size={14} /> Call
                </a>
              )}

              {/* Send Message button — always visible (uses in-app messaging) */}
              <button
                onClick={handleContactClick}
                className="px-4 py-2.5 bg-gray-900 text-white rounded-xl text-sm font-semibold whitespace-nowrap min-h-[44px] flex items-center gap-1.5 active:bg-gray-700 transition-colors"
              >
                <Mail size={14} /> Message
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* =============================================
          GROWTH-01 — WEB-TO-APP PROMPT (mobile web only)
          Client-only: renders nothing on the server and nothing on the
          first client render, so this page's SSR HTML is unchanged and
          hydration has nothing to disagree about. Never renders in the
          Capacitor app. Overlays above the sticky bar; no layout shift.
          ============================================= */}
      <MobileAppPrompt pageType="property" contentId={property.id} />

      {/* =============================================
          FULLSCREEN IMAGE LIGHTBOX (unified gallery viewer)
          ============================================= */}
      {lightboxOpen && images.length > 0 && (
        <ImageLightbox
          images={images}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxOpen(false)}
          title={property?.title || ''}
        />
      )}

      {/* =============================================
          SHARE MODAL
          ============================================= */}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        propertyUrl={propertyUrl}
        propertyTitle={property.title}
      />

      {/* =============================================
          CONTACT AGENT MODAL (lazy-loaded, creates lead in DB)
          ============================================= */}
      {showContactModal && (
        <ContactAgentModalSSR
          isOpen={showContactModal}
          onClose={() => setShowContactModal(false)}
          listingId={property.id}
          agentId={property.agentId}
          agentName={property.agent.name}
          propertyTitle={property.title}
          user={userId ? { id: userId, email: userEmail || undefined } : null}
        />
      )}

      {/* =============================================
          AUTH PROMPT MODAL (inline, shown to guests)
          ============================================= */}
      {showAuthPrompt && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={() => setShowAuthPrompt(false)}
        >
          <div
            className="bg-white rounded-3xl max-w-md w-full p-8 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center">
              {/* Icon */}
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="text-green-700" size={32} />
              </div>

              {/* Title & Description */}
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                Create an account to continue
              </h2>
              <p className="text-gray-600 mb-6">
                Sign up for free to contact agents and save your favorite properties.
              </p>

              {/* Buttons */}
              <div className="space-y-3">
                <a
                  href="/auth?mode=signup&role=buyer"
                  className="block w-full py-3.5 bg-green-700 text-white rounded-xl font-semibold hover:bg-green-800 transition-colors text-center"
                  onClick={() => setShowAuthPrompt(false)}
                >
                  Create Free Account
                </a>

                <a
                  href="/auth?mode=login"
                  className="block w-full py-3.5 border border-gray-200 rounded-xl font-semibold text-gray-700 hover:bg-gray-50 transition-colors text-center"
                  onClick={() => setShowAuthPrompt(false)}
                >
                  Already have an account? Log In
                </a>
              </div>

              {/* Terms */}
              <p className="mt-4 text-xs text-gray-500">
                By signing up, you agree to our{' '}
                <a href="/terms" className="text-green-700 hover:underline">Terms of Service</a>
              </p>

              {/* Close button */}
              <button
                onClick={() => setShowAuthPrompt(false)}
                className="mt-4 text-sm text-gray-500 hover:text-gray-700"
              >
                Maybe later
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
